/**
 * Cave thumbnails for menus (setup, practice, daily, the kit's library): the cave drawn with its
 * real sprites, then shrunk with smoothing, so dirt, rock and diamonds read as their colours.
 */

import type { BuiltCave } from '../caves/types';
import { E } from '../sim/elements';
import type { CaveState } from '../sim/types';
import { atlasFor } from './atlas';
import { makeCanvas } from './canvas';
import { cellLook } from './cellSprite';
import { CELL } from './sprites/types';

const cache = new Map<string, HTMLCanvasElement>();

function fakeState(b: BuiltCave): CaveState {
  // Enough of a state for `cellLook`: nothing is moving, the magic wall is dormant.
  return {
    w: b.w,
    h: b.h,
    cells: b.cells,
    digby: { x: 0, y: 0, born: false, alive: true, facing: 1, moving: false, pushing: false, idle: 0 },
    magic: { phase: 'dormant', msLeft: 0 },
    scan: 0,
  } as unknown as CaveState;
}

/** A `w * px` by `h * px` picture of the built cave. Cached per cave, level and size. */
export function thumbnail(b: BuiltCave, px: number): HTMLCanvasElement {
  const key = `${b.def.id}|${b.def.name}|${b.level}|${px}|${b.colors.join(',')}|${b.w}x${b.h}|${b.cells.length}|${checksum(b.cells)}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const atlas = atlasFor(b.colors);
  const full = makeCanvas(b.w * CELL, b.h * CELL);
  const fctx = full.getContext('2d');
  const out = makeCanvas(b.w * px, b.h * px);
  const octx = out.getContext('2d');
  if (!fctx || !octx) return out;
  fctx.fillStyle = '#000';
  fctx.fillRect(0, 0, full.width, full.height);
  const st = fakeState(b);
  for (let y = 0; y < b.h; y++) {
    for (let x = 0; x < b.w; x++) {
      const el = b.cells[y * b.w + x] ?? E.STEEL;
      // The exit shows as a door, so a menu picture tells you where it is.
      const look = el === E.OUTBOX_CLOSED ? { sprite: 'door' as const, frame: 0 } : cellLook(el, st, 0);
      if (look.sprite) atlas.draw(fctx, look.sprite, look.frame, x * CELL, y * CELL);
    }
  }
  octx.imageSmoothingEnabled = true;
  octx.imageSmoothingQuality = 'high';
  octx.drawImage(full, 0, 0, out.width, out.height);
  cache.set(key, out);
  return out;
}

function checksum(cells: Uint8Array): number {
  let h = 0;
  for (let i = 0; i < cells.length; i++) h = (Math.imul(h, 31) + (cells[i] ?? 0)) >>> 0;
  return h;
}
